import React, { useState, useEffect } from 'react'
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Calendar,
  RefreshCw,
  FileText,
  CreditCard,
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, StatsCard } from '../../components/ui'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
} from 'recharts'
import {
  apiService,
  FinancialOverview,
  CashFlowData,
  FinancialKPIs,
  PendingFinancials,
} from '../../services/api'

export const FinancialDashboardPage: React.FC = () => {
  const [overview, setOverview] = useState<FinancialOverview | null>(null)
  const [cashFlow, setCashFlow] = useState<CashFlowData[]>([])
  const [kpis, setKPIs] = useState<FinancialKPIs | null>(null)
  const [pending, setPending] = useState<PendingFinancials | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [dateFilter, setDateFilter] = useState<{ start?: string; end?: string }>({})

  const loadFinancialData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      setError(null)

      const [overviewData, cashFlowData, kpisData, pendingData] = await Promise.all([
        apiService.getFinancialOverview(dateFilter.start, dateFilter.end),
        apiService.getFinancialCashFlow(6),
        apiService.getFinancialKPIs(dateFilter.start, dateFilter.end),
        apiService.getFinancialPending(),
      ])

      setOverview(overviewData)
      setCashFlow(cashFlowData)
      setKPIs(kpisData)
      setPending(pendingData)
    } catch (err) {
      console.error('Erro ao carregar dados financeiros:', err)
      setError('Não foi possível carregar os dados financeiros')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    void loadFinancialData()
  }, [dateFilter])

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`
  }

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const setCurrentMonth = () => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]
    setDateFilter({ start, end })
  }

  const setCurrentYear = () => {
    const now = new Date()
    const start = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0]
    const end = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0]
    setDateFilter({ start, end })
  }

  const clearFilters = () => {
    setDateFilter({})
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => void loadFinancialData()}
          className="px-4 py-2 bg-accent text-white rounded-md hover:bg-accent/90"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard Financeiro</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Visão geral completa das finanças da sua concessionária
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => void loadFinancialData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-accent/10 text-accent rounded-md hover:bg-accent/20 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {/* Period Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-muted-foreground">Período:</span>
            <button
              onClick={setCurrentMonth}
              className="px-3 py-1 text-sm bg-accent/10 text-accent rounded-md hover:bg-accent/20"
            >
              Mês Atual
            </button>
            <button
              onClick={setCurrentYear}
              className="px-3 py-1 text-sm bg-accent/10 text-accent rounded-md hover:bg-accent/20"
            >
              Ano Atual
            </button>
            {(dateFilter.start || dateFilter.end) && (
              <button
                onClick={clearFilters}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Limpar Filtros
              </button>
            )}
            {dateFilter.start && (
              <span className="text-sm text-muted-foreground">
                {formatDate(dateFilter.start)} - {dateFilter.end ? formatDate(dateFilter.end) : 'Hoje'}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatsCard
            title="Receita Total"
            value={formatCurrency(overview.totalRevenue)}
            icon={DollarSign}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-500/10"
          />
          <StatsCard
            title="Despesas Totais"
            value={formatCurrency(overview.totalExpenses)}
            icon={TrendingDown}
            iconColor="text-red-600"
            iconBg="bg-red-500/10"
          />
          <StatsCard
            title="Lucro Líquido"
            value={formatCurrency(overview.netProfit)}
            icon={TrendingUp}
            iconColor={overview.netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}
            iconBg={overview.netProfit >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}
          />
          <StatsCard
            title="Margem de Lucro"
            value={formatPercentage(overview.profitMargin)}
            icon={TrendingUp}
            iconColor={overview.profitMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}
            iconBg={overview.profitMargin >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}
          />
        </div>
      )}

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Ticket Médio</div>
              <div className="text-2xl font-bold text-foreground">{formatCurrency(kpis.averageTicket)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Taxa de Inadimplência</div>
              <div className="text-2xl font-bold text-foreground">{formatPercentage(kpis.defaultRate)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Margem Bruta</div>
              <div className="text-2xl font-bold text-foreground">{formatPercentage(kpis.grossMargin)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-muted-foreground mb-1">Margem Operacional</div>
              <div className="text-2xl font-bold text-foreground">{formatPercentage(kpis.operatingMargin)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cash Flow Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Fluxo de Caixa (Últimos 6 Meses)</CardTitle>
        </CardHeader>
        <CardContent>
          {cashFlow.length > 0 ? (
            <div style={{ width: '100%', height: '350px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cashFlow}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#10B981" name="Receita" strokeWidth={2} />
                  <Line type="monotone" dataKey="expenses" stroke="#EF4444" name="Despesas" strokeWidth={2} />
                  <Line type="monotone" dataKey="netFlow" stroke="#3B82F6" name="Fluxo Líquido" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[350px]">
              <p className="text-sm text-muted-foreground">Nenhum dado disponível</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Financials */}
      {pending && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Overdue Invoices */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                Faturas Vencidas ({pending.overdueInvoices.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pending.overdueInvoices.length > 0 ? (
                <div className="space-y-3">
                  {pending.overdueInvoices.map((invoice) => (
                    <div key={invoice.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <p className="font-medium text-foreground">{invoice.number}</p>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{invoice.customerName}</p>
                        <p className="text-xs text-red-600 mt-1">{invoice.daysOverdue} dias de atraso</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-foreground">{formatCurrency(invoice.amount)}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(invoice.dueDate)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-muted-foreground">Nenhuma fatura vencida</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Payments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Próximos Pagamentos ({pending.upcomingPayments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pending.upcomingPayments.length > 0 ? (
                <div className="space-y-3">
                  {pending.upcomingPayments.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                          <p className="font-medium text-foreground">{payment.description}</p>
                        </div>
                        <p className="text-xs text-blue-600 mt-1">Vence em {payment.daysUntilDue} dias</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-foreground">{formatCurrency(payment.amount)}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(payment.dueDate)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-muted-foreground">Nenhum pagamento próximo</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
